import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  listTemplateStylePreferences,
  updateTemplateStylePreferences,
} from '../../store/actions/memory';
import {
  Card,
  Typography,
  Select,
  Divider,
  Tooltip,
  Badge,
  Switch,
  Popover,
  Empty,
} from 'antd';
import {
  DeleteOutlined,
  SaveOutlined,
  CloseOutlined,
  InfoCircleOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import TemplateSelectDropdown from '../Settings/Settings.TemplateDropdown';
import {
  listTemplates,
  listTemplateOverrides,
} from '../../store/actions/settings';
import { useAuth, useFeatureEntitlements } from '@frontegg/react';
import { updateCurrentPractitionerSettings } from '../../../../store/actions/practitioner.action';
import { showToastSuccess } from '../../../../utilities/toast';
import { ModalComponent } from '../baseComponents';
import { AlertIcon } from '../../../../icons';
import { SupaDemo } from '../../../../components/groupComponents/supaDemo';
import { analytics } from 'src/services';

const { Title, Text } = Typography;

const TemplateSelector = ({
  selectedTemplate,
  setSelectedTemplate,
  lastModified,
  isDisabled,
  verbosityFilter,
  setVerbosityFilter,
}) => {
  const { user } = useAuth();

  const formatLastUpdated = (timestamp) => {
    if (!timestamp) return 'Never';
    return moment(timestamp).format('MM/DD/YYYY, h:mm:ss A');
  };

  return (
    <div className="mb-6 items-center gap-4">
      <div className="w-full flex mb-4">
        <label
          htmlFor="template-select"
          className="text-black text-[18px] flex items-center"
        >
          Select note template{' '}
          <SupaDemo>
            <span
              data-supademo-demo="clz07qg9i2ibdz9kd5sj5nz70"
              className="ml-4 cursor-pointer ih-button-ghost gap-2 text-[#5a8dda] underline"
              onClick={() => {
                analytics.track('Clicked Custom Template Guided Tour ', {
                  email: user?.email,
                });
              }}
            >
              <QuestionCircleOutlined /> Guided Tour
            </span>
          </SupaDemo>
        </label>
      </div>
      <div className="w-full flex gap-4">
        <div className="lg:w-1/2 w-full">
          <TemplateSelectDropdown
            restrictTemplates={isDisabled}
            allowInlineEdit={false}
            encounterPhase="in-visit"
            currentTemplate={selectedTemplate}
            setCurrentTemplate={setSelectedTemplate}
            setNextWorkingTemplate={setSelectedTemplate}
            fromIsSettings={false}
          />
        </div>
        <div className="lg:w-1/4 w-full">
          <VerbosityFilter
            verbosityFilter={verbosityFilter}
            setVerbosityFilter={setVerbosityFilter}
          />
        </div>
      </div>
    </div>
  );
};

const ObservationCard = ({
  observation,
  isEditing,
  onEdit,
  onSave,
  onCancel,
  onDelete,
  section,
  index,
  isDisabled,
}) => (
  <Card>
    <div className="flex justify-between items-center">
      {isEditing ? (
        <>
          <input
            type="text"
            className="flex-1 mr-2 px-2 py-1 border rounded"
            defaultValue={observation.description}
            data-section={section}
            data-index={index}
            disabled={isDisabled}
            autoFocus
          />
          <div className="flex gap-2">
            <SaveOutlined
              className={`${
                isDisabled ? 'text-gray-300' : 'text-green-500'
              } cursor-${isDisabled ? 'not-allowed' : 'pointer'}`}
              onClick={!isDisabled ? onSave : undefined}
            />
            <CloseOutlined
              className={`${
                isDisabled ? 'text-gray-300' : 'text-red-500'
              } cursor-${isDisabled ? 'not-allowed' : 'pointer'}`}
              onClick={!isDisabled ? onCancel : undefined}
            />
          </div>
        </>
      ) : (
        <>
          <div>{observation.description}</div>
          <div className="flex gap-2">
            {/* Disable edit for now */}
            {/* <EditOutlined
              className={`cursor-${isDisabled ? 'not-allowed' : 'pointer'} ${
                isDisabled ? 'text-gray-300' : ''
              }`}
              onClick={!isDisabled ? onEdit : undefined}
            /> */}
            <DeleteOutlined
              className={`cursor-${isDisabled ? 'not-allowed' : 'pointer'} ${
                isDisabled ? 'text-gray-300' : 'text-red-500'
              } ml-2`}
              onClick={!isDisabled ? onDelete : undefined}
            />
          </div>
        </>
      )}
    </div>
  </Card>
);

const DeleteConfirmationModal = ({ visible, onConfirm, onCancel }) => (
  <ModalComponent
    centered={true}
    open={visible}
    handleSubmit={onConfirm}
    handleCancel={onCancel}
    submitButtonText="Delete"
    cancelButtonText="Cancel"
    closable={false}
    className="!w-fit"
    additionalBody={
      <>
        <div className="text-black font-sans font-medium text-lg flex justify-center items-center gap-2">
          <span>
            <AlertIcon />
          </span>
          <p>Are you sure you want to delete this observation?</p>
        </div>
      </>
    }
  />
);

const VerbosityFilter = ({ verbosityFilter, setVerbosityFilter }) => {
  const { isEntitled: isMemoryEntitled } = useFeatureEntitlements('memory');
  return (
    <div>
      <Select
        bordered={false}
        value={verbosityFilter}
        onChange={setVerbosityFilter}
        className="mt-1 mr-5 border border-[#d9d9d9] bg-white rounded-xl shadow-sm py-2 outline-none w-full h-10 flex items-center font-sans note-template"
        options={[
          { value: 'comprehensive', label: 'Comprehensive' },
          { value: 'balanced', label: 'Balanced' },
          { value: 'concise', label: 'Concise' },
        ]}
        disabled={!isMemoryEntitled}
      />
    </div>
  );
};

const TemplateMemory = () => {
  const dispatch = useDispatch();
  const [selectedPreference, setSelectedPreference] = useState(0);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const { templateStylePreferences, isTemplateStylePreferencesLoading } =
    useSelector((state) => state.memoryState);
  const [verbosityFilter, setVerbosityFilter] = useState('comprehensive');
  const [editingState, setEditingState] = useState({
    section: null,
    index: null,
  });
  const [deleteModal, setDeleteModal] = useState({
    visible: false,
    section: null,
    index: null,
  });
  const [localPreferences, setLocalPreferences] = useState(null);
  const { user } = useAuth();
  const { currentPractitionerSettings } = useSelector((state) => ({
    ...state?.practitionerState,
  }));
  const isMemoryDisabled =
    !currentPractitionerSettings?.note_preferences?.memory;
  const { isEntitled: isMemoryEntitled } = useFeatureEntitlements('memory');

  useEffect(() => {
    dispatch(listTemplates());
    dispatch(listTemplateOverrides());
    dispatch(listTemplateStylePreferences());
    analytics.track('Viewed Memory Prefererencess', { email: user?.email });
  }, [dispatch]);

  useEffect(() => {
    if (selectedTemplate) {
      const preferenceIndex = templateStylePreferences?.findIndex(
        (pref) =>
          (pref.template_id === selectedTemplate.template_id ||
            pref.template_overrides_id ===
              selectedTemplate.template_overrides_id) &&
          pref.verbosity === verbosityFilter
      );
      setSelectedPreference(preferenceIndex);
    }
  }, [selectedTemplate, templateStylePreferences, verbosityFilter]);

  useEffect(() => {
    analytics.track('Changed Template In Memory Preferences', {
      email: user?.email,
    });
    if (templateStylePreferences?.[selectedPreference]) {
      setLocalPreferences(
        templateStylePreferences[selectedPreference].preferences
      );
    } else {
      setLocalPreferences(null);
    }
  }, [templateStylePreferences, selectedPreference]);

  const formatLastUpdated = (timestamp) => {
    if (!timestamp) return 'Never';
    return moment(timestamp).format('MM/DD/YYYY, h:mm:ss A');
  };

  const handleEdit = (section, observationIndex) => {
    analytics.track('Clicked On Edit In Memory Prefererences', {
      email: user?.email,
    });
    setEditingState({
      section,
      index: observationIndex,
    });
  };

  const handleSave = (section, index) => {
    analytics.track('Clicked On Save In Memory Prefererences', {
      email: user?.email,
    });
    const newDescription = document.querySelector(
      `input[data-section="${section}"][data-index="${index}"]`
    ).value;

    const updatedPreferences = {
      ...localPreferences,
      [section]: [
        ...localPreferences[section].slice(0, index),
        {
          ...localPreferences[section][index],
          description: newDescription,
        },
        ...localPreferences[section].slice(index + 1),
      ],
    };

    setLocalPreferences(updatedPreferences);
    dispatch(
      updateTemplateStylePreferences(
        templateStylePreferences[selectedPreference].id,
        updatedPreferences
      )
    );
    setEditingState({ section: null, index: null });
  };

  const handleDelete = (section, observationIndex) => {
    analytics.track('Clicked On Delete In Delete Memory Prefererences', {
      email: user?.email,
    });
    setDeleteModal({
      visible: true,
      section,
      index: observationIndex,
    });
  };

  const confirmDelete = () => {
    analytics.track(
      'Clicked On Confirm Delete In Delete Memory Prefererences',
      { email: user?.email }
    );
    const updatedPreferences = {
      ...localPreferences,
      [deleteModal.section]: localPreferences[deleteModal.section].filter(
        (_, index) => index !== deleteModal.index
      ),
    };

    setLocalPreferences(updatedPreferences);
    dispatch(
      updateTemplateStylePreferences(
        templateStylePreferences[selectedPreference].id,
        updatedPreferences,
        () => {
          showToastSuccess('Successfully deleted observation');
          setDeleteModal({ visible: false, section: null, index: null });
        }
      )
    );
  };

  return (
    <div>
      <Title level={4} className="m-0 mr-2">
        Template Preferences
      </Title>

      <Text type="secondary" className="mb-6 block">
        Autogenerated based on differences between AI generated and edited
        summaries for each template.
      </Text>
      <TemplateSelector
        selectedTemplate={selectedTemplate}
        setSelectedTemplate={setSelectedTemplate}
        lastModified={
          templateStylePreferences?.[selectedPreference]?.last_modified
        }
        isDisabled={!isMemoryEntitled}
        verbosityFilter={verbosityFilter}
        setVerbosityFilter={setVerbosityFilter}
      />
      <h1 className="text-lg text-gray-900 mb-4">Sections</h1>
      {!isMemoryEntitled && <div>Upgrade to view your memory preferences.</div>}
      {isMemoryEntitled && !localPreferences && (
        <Empty description="No preferences found yet." />
      )}
      {isMemoryEntitled &&
        localPreferences &&
        Object.entries(localPreferences)
          .filter(([_, observations]) => observations.length > 0)
          .map(([section, observations]) => (
            <div key={section} className="mb-6">
              <Title level={5} className="mb-4">
                {section.toUpperCase()}
              </Title>
              <div className="space-y-4">
                {observations.map((observation, index) => (
                  <ObservationCard
                    key={index}
                    observation={observation}
                    isEditing={
                      editingState.section === section &&
                      editingState.index === index
                    }
                    onEdit={() => handleEdit(section, index)}
                    onSave={() => handleSave(section, index)}
                    onCancel={() =>
                      setEditingState({ section: null, index: null })
                    }
                    onDelete={() => handleDelete(section, index)}
                    section={section}
                    index={index}
                    isDisabled={isMemoryDisabled}
                  />
                ))}
              </div>
            </div>
          ))}
      <div className="flex flex-col justify-end items-end">
        <Typography.Text type="secondary" className="text-xs">
          Last Updated at{' '}
          {formatLastUpdated(
            templateStylePreferences?.[selectedPreference]?.last_modified
          )}
        </Typography.Text>
      </div>
      <DeleteConfirmationModal
        visible={deleteModal.visible}
        onConfirm={confirmDelete}
        onCancel={() =>
          setDeleteModal({ visible: false, section: null, index: null })
        }
      />
    </div>
  );
};

export default TemplateMemory;
